import type { ApolloCache, Reference } from '@apollo/client';
import type { Modifier } from '@apollo/client/cache/core/types/common';
import type { AsStoreObject } from '@apollo/client/utilities';
import { readFieldIfReference } from '@frameio/graphql-client';
import {
  type AssetDeletedFragment,
  type FolderAssetsConnection,
  type MovedAssetFragment,
} from '@frameio/graphql-client/generated';
import type { GetFolderAssetsQueryVariables } from '../../shared/gql/generated/GetFolderAssets';
import { parseStoreFieldName } from './utils/parseStoreFieldName';

type RemovedAsset = AssetDeletedFragment | MovedAssetFragment;

/**
 * Removes assets from the cached folderAssets set for a given folder.
 *
 * @param cache the apollo cache instance
 * @param sourceFolderId the id of the source folder
 * @param assets the array of assets to remove
 */
export const removeAssetsFromFolderAssets = (
  cache: ApolloCache<any>,
  sourceFolderId: string,
  assets: RemovedAsset[]
) => {
  const folderIds = new Set<string>();
  const fileIds = new Set<string>();

  assets.forEach((asset) => {
    if (asset.__typename === 'FolderAsset') {
      folderIds.add(asset.id);
    } else {
      fileIds.add(asset.id);
    }
  });

  // instantly remove the moved assets from the source folder
  const folderAssets: Modifier<
    Reference | AsStoreObject<FolderAssetsConnection>
  > = (value, { isReference, readField, storeFieldName }) => {
    const originalValue = readFieldIfReference({
      fieldName: 'folders',
      isReference,
      readField,
      value,
    });

    if (!originalValue) return value;

    const variables =
      parseStoreFieldName<GetFolderAssetsQueryVariables>(storeFieldName);

    if (!variables) return value;

    let nodes = originalValue.nodes;

    if (nodes === undefined) {
      return value;
    }

    if (variables.assetType === 'FOLDER') {
      nodes = nodes.filter((asset) => !folderIds.has(asset?.id ?? ''));
    } else {
      nodes = nodes.filter((asset) => !fileIds.has(asset?.id ?? ''));
    }

    return {
      ...originalValue,
      nodes: [...nodes],
    };
  };

  cache.modify({
    id: cache.identify({ __typename: 'FolderAsset', id: sourceFolderId }),
    fields: {
      assets: folderAssets,
    },
  });
};
